"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSItemDef = exports.ENTERABLE_ITEM_TYPES = void 0;
const localizeText_ts_1 = require("../localizeText.js");
const errors_ts_1 = require("../errors.js");
/** Item types a form renders (data-model `entryType` + the structured types). Anything else is never offered. */
exports.ENTERABLE_ITEM_TYPES = [
    'number', 'text', 'select', 'checkbox', 'date', 'picture',
    'composite', 'datasource-search', 'convertible', 'slider', 'multi-select'
];
class HDSItemDef {
    #data;
    #key;
    /**
     * Optional model handle, used to validate descendant streamIds in
     * `eventTemplate({ context })`. Constructed by HDSModelItemsDefs which has
     * the model handle available; older callers that build HDSItemDef directly
     * still work — they just can't use the `context` option.
     */
    #model;
    constructor(key, definitionData, model = null) {
        this.#key = key;
        this.#data = definitionData;
        this.#model = model;
    }
    get eventTypes() {
        if (this.#data.eventType)
            return [this.#data.eventType];
        return this.#data.variations.eventType.options.map((o) => o.value);
    }
    get key() {
        return this.#key;
    }
    get data() {
        return this.#data;
    }
    get repeatable() {
        return this.#data.repeatable || 'unlimited';
    }
    /**
     * Whether this item is deprecated. Deprecated items remain readable
     * (existing events keep validating + rendering) but should not be
     * surfaced in UIs that let users create new events. See
     * `data-model/AGENTS.md § "deprecated: true on items"`.
     */
    get isDeprecated() {
        return this.#data.deprecated === true;
    }
    /**
     * Whether this is a system item (`type: system`, e.g. `sync-status`). System
     * items carry state written by software (a connector's status), not data a
     * user enters: they stay readable and requestable like any item, but must not
     * be offered in pickers or rendered as form fields. See
     * `data-model/documentation/SYNC-STATUS.md`.
     */
    get isSystem() {
        return this.#data.type === 'system';
    }
    /**
     * Whether this is a document item (`type: attachments`, data-model 3.25.0): files carried as Pryv attachments,
     * with metadata as content (`file/xds-v1`). Imported, never entered field by field: readable and requestable
     * like any item, but not offered in pickers or rendered as an editable form field. See
     * `data-model/documentation/DOCUMENTS.md`.
     */
    get isAttachments() {
        return this.#data.type === 'attachments';
    }
    /**
     * Whether this is an imported item (`type: imported`, data-model 3.27.0): an object written by an importer from a
     * coded source (`lab-result/coded-v1`), never entered in a form. Read, shown and requested like any item (unlike
     * `system`), but not offered in pickers or rendered as an editable form field.
     */
    get isImported() {
        return this.#data.type === 'imported';
    }
    /**
     * Whether a person enters this item field by field: its `type` is one a form renders. False for `system`,
     * `attachments`, `imported` and any type this version does not know (a newer pack), so a picker never offers an
     * item it cannot render.
     */
    get isEnterable() {
        return exports.ENTERABLE_ITEM_TYPES.includes(this.#data.type);
    }
    get reminder() {
        return this.#data.reminder || null;
    }
    /** label Localized */
    get label() {
        return (0, localizeText_ts_1.localizeText)(this.#data.label);
    }
    /** description Localized */
    get description() {
        return (0, localizeText_ts_1.localizeText)(this.#data.description);
    }
    /**
     * a template event with eventType and streamIds
     *
     * @param opts.context — optional context streamId per Plan 46 §2.1 (D3).
     *   Must equal `this.streamId` or be a descendant. Lets a single itemDef
     *   registered at e.g. `treatment` produce events placed at `treatment-fertility`,
     *   `treatment-oncology`, etc., without per-domain item definitions.
     *   When omitted, falls back to the itemDef's canonical streamId.
     *   Throws if the context isn't in the itemDef's subtree.
     *
     * @param opts.eventType — REQUIRED for an itemDef declaring `variations.eventType`
     *   (`body-weight`, `body-height`, `body-blood-serum-glucose-fasting`, `profile-avatar`).
     *   Must be one of the declared options.
     *
     * For a variation item the option **is** the stored value's meaning: `mass/kg` vs
     * `mass/lb` is the difference between 75 kg and 75 lb. Until 2.0.0 this returned
     * `eventTypes[0]` whatever the caller meant, so a weight entered in pounds was stored
     * as kilograms with nothing failing anywhere (issue #13). It now throws instead: a
     * caller that has not chosen cannot express intent, and guessing on their behalf is
     * what produced wrong clinical values.
     *
     * Deliberately NOT resolved from `unitSystem` here. That would couple this primitive
     * to ambient settings state and return a different unit for the same itemDef depending
     * on whether an app had hooked its settings, which is a quieter version of the same
     * bug. Callers that want the user's preference resolve it themselves (see
     * `HDSModelPreferred`) and pass the result in.
     */
    eventTemplate(opts = {}) {
        let chosenStreamId = this.#data.streamId;
        if (opts.context != null && opts.context !== chosenStreamId) {
            this.#assertDescendantOf(opts.context, chosenStreamId);
            chosenStreamId = opts.context;
        }
        return {
            streamIds: [chosenStreamId],
            type: this.#chooseEventType(opts.eventType)
        };
    }
    /**
     * Resolve the event type for `eventTemplate()`, refusing to guess.
     * Variation items require an explicit choice; plain items reject a mismatched one.
     */
    #chooseEventType(requested) {
        const isVariation = this.#data.variations?.eventType != null;
        const options = this.eventTypes;
        if (!isVariation) {
            if (requested != null && requested !== options[0]) {
                throw new errors_ts_1.HDSLibError(`eventTemplate: item "${this.#key}" declares eventType "${options[0]}" ` +
                    `and cannot produce "${requested}".`);
            }
            return options[0];
        }
        if (requested == null) {
            throw new errors_ts_1.HDSLibError(`eventTemplate: item "${this.#key}" declares variations.eventType and requires an ` +
                `explicit choice. Pass one of: ${options.join(', ')}. ` +
                'The option is the stored unit, so choosing for you would risk storing a wrong value.');
        }
        if (!options.includes(requested)) {
            throw new errors_ts_1.HDSLibError(`eventTemplate: "${requested}" is not a declared variation of item "${this.#key}". ` +
                `Expected one of: ${options.join(', ')}.`);
        }
        return requested;
    }
    /**
     * Throws if `candidate` is not a descendant of `ancestor` in the model's
     * stream tree. Requires the model handle (passed at construction).
     */
    #assertDescendantOf(candidate, ancestor) {
        if (!this.#model) {
            throw new Error(`HDSItemDef "${this.#key}" was constructed without a model handle; cannot validate context "${candidate}"`);
        }
        // getParentsIds returns ancestors (excluding self). Treat candidate as
        // valid iff `ancestor` is in its parent chain.
        const ancestors = this.#model.streams.getParentsIds(candidate, false);
        if (!ancestors.includes(ancestor)) {
            throw new Error(`Context streamId "${candidate}" is not a descendant of itemDef "${this.#key}" streamId "${ancestor}"`);
        }
    }
    /**
     * D3-aware event-matching. Returns true if the given event resolves to
     * this itemDef via `model.itemsDefs.forEvent(event)` — covering both the
     * direct (streamId, eventType) match and the closest-ancestor walk-up.
     *
     * Useful in form-engine code that needs to check whether an event belongs
     * to a particular itemDef without re-implementing the resolution rule.
     * Falls back to plain `(streamId in event.streamIds, type === eventType)`
     * if the model handle isn't available.
     */
    /**
     * Event types that count as "this concept was recorded", including the ones
     * belonging to same-stream twins.
     *
     * **Why this is not just `eventTypes`.** A concept can be recorded at more
     * than one fidelity on the same stream: a presence marker
     * (`symptom-pain-headache`, `activity/plain`) for sources that only know an
     * occurrence, and a graded twin (`symptom-pain-headache-severity`,
     * `ratio/proportion`). A form that asks for the graded item is still
     * *satisfied* by a bridge that can only report presence — asking the subject
     * to re-enter what a working bridge already sent is the bug this exists for
     * (`B-2026-09-15-5`).
     *
     * **Use it for "has this been recorded?" — completion, reminders, task
     * prompts. Never use it to read a value.** The types collected here are
     * deliberately *not* interchangeable as data: `body-urine-hormones-lh`
     * (IU/L) and `fertility-hormone-lh` (mg/L) share a stream, and reading one
     * as the other would be wrong by a factor that looks plausible. Resolve the
     * event through `forEvent` before interpreting its content.
     *
     * Falls back to this item's own `eventTypes` when the itemDef was built
     * without a model handle.
     */
    get satisfyingEventTypes() {
        if (!this.#model)
            return this.eventTypes;
        const streamId = this.#data.streamId;
        if (typeof streamId !== 'string')
            return this.eventTypes;
        const types = new Set(this.eventTypes);
        for (const sibling of this.#model.itemsDefs.forStreamId(streamId)) {
            for (const t of sibling.eventTypes)
                types.add(t);
        }
        return [...types];
    }
    /**
     * Whether `event` counts as this concept having been recorded, accepting a
     * same-stream twin. See {@link satisfyingEventTypes} for the read-vs-record
     * distinction — this answers "recorded?", not "what value?".
     */
    satisfiedByEvent(event) {
        if (!event || event.type == null || !Array.isArray(event.streamIds))
            return false;
        if (!this.satisfyingEventTypes.includes(event.type))
            return false;
        return event.streamIds.includes(this.#data.streamId);
    }
    matchesEvent(event) {
        if (!event || event.type == null || !Array.isArray(event.streamIds))
            return false;
        if (this.#model) {
            const resolved = this.#model.itemsDefs.forEvent(event, false);
            return resolved !== null && resolved.key === this.#key;
        }
        // Fallback: direct match against this itemDef's streamId + eventType.
        if (!this.eventTypes.includes(event.type))
            return false;
        return event.streamIds.includes(this.#data.streamId);
    }
}
exports.HDSItemDef = HDSItemDef;
